import React from 'react';
import { useTranslation } from 'react-i18next';
import styles from './AuthShell.module.css';
import { artavaBrand } from '../../branding/artavaBrand';
import { getAuthCopy } from './authCopy';

export default function AuthShell({
  variant = 'signin',
  eyebrow,
  title,
  subtitle,
  children,
}) {
  const { i18n } = useTranslation();
  const copy = getAuthCopy(i18n.language);
  const isCompact = variant === 'signin';
  const isRegister = variant === 'register';
  const content = isRegister
    ? copy.shell.variants.signin
    : (copy.shell.variants[variant] || copy.shell.variants.signin);
  const isItalian = i18n.language?.startsWith('it');

  return (
    <div className={`${styles.page} ${isRegister ? styles.pageRegister : ''}`}>
      <main className={`${styles.shell} ${isCompact ? styles.shellCompact : ''} ${isRegister ? styles.shellRegister : ''}`}>
        <section className={`${styles.formColumn} ${isCompact ? styles.formColumnCompact : ''}`}>
          <div className={`${styles.formCard} ${isCompact ? styles.formCardCompact : ''} ${isRegister ? styles.formCardRegister : ''}`}>
            <div className={styles.brandBar}>
              <div className={styles.brandCluster}>
                <div className={styles.productCluster}>
                  <img src="/smarti-mark.svg" alt="" className={styles.productLogo} />
                  <div>
                    <p className={styles.productLabel}>Smarti</p>
                    <p className={styles.productMeta}>{copy.shell.productMeta}</p>
                  </div>
                </div>
              </div>

              <div className={styles.brandActions}>
                <div className={styles.languageSwitch} role="group" aria-label={copy.shell.languageLabel}>
                  <button
                    type="button"
                    className={`${styles.languageButton} ${isItalian ? styles.languageButtonActive : ''}`}
                    onClick={() => i18n.changeLanguage('it')}
                  >
                    IT
                  </button>
                  <button
                    type="button"
                    className={`${styles.languageButton} ${!isItalian ? styles.languageButtonActive : ''}`}
                    onClick={() => i18n.changeLanguage('en')}
                  >
                    EN
                  </button>
                </div>

                <a
                  className={styles.supportLink}
                  href={artavaBrand.websiteUrl}
                  target="_blank"
                  rel="noreferrer"
                >
                  {copy.shell.websiteShort}
                </a>
              </div>
            </div>

            <div className={styles.formIntro}>
              {eyebrow ? <p className={styles.eyebrow}>{eyebrow}</p> : null}
              <h1 className={styles.title}>{title}</h1>
              {subtitle ? <p className={styles.subtitle}>{subtitle}</p> : null}
            </div>

            {children}

            <div className={`${styles.footerBar} ${isCompact ? styles.footerBarCompact : ''}`}>
              <a href={`mailto:${artavaBrand.contactEmail}`}>{copy.shell.footerEmail}</a>
              <a href={artavaBrand.websiteUrl} target="_blank" rel="noreferrer">{copy.shell.footerWebsite}</a>
              <a
                href={artavaBrand.linkedInUrl}
                target="_blank"
                rel="noreferrer"
              >
                {copy.shell.footerLinkedIn}
              </a>
            </div>
          </div>
        </section>

        <aside className={`${styles.storyColumn} ${isCompact ? styles.storyColumnCompact : ''} ${isRegister ? styles.storyColumnRegister : ''}`}>
          <div className={`${styles.storySurface} ${isCompact ? styles.storySurfaceCompact : ''} ${isRegister ? styles.storySurfaceRegister : ''}`}>
            <p className={styles.storyBadge}>{content.badge}</p>
            <h2 className={styles.storyTitle}>{content.title}</h2>
            <p className={styles.storyText}>{content.body}</p>

            <div className={styles.storySignature}>
              <img src={artavaBrand.logoSrc} alt={artavaBrand.companyName} className={styles.storySignatureLogo} />
              <div className={styles.storySignatureCopy}>
                <span>{isItalian ? 'Sviluppato da' : 'Built by'}</span>
                <strong>{artavaBrand.companyName}</strong>
              </div>
            </div>

            <div className={`${styles.metricsGrid} ${isCompact ? styles.metricsGridCompact : ''}`}>
              {content.metrics.map((metric) => (
                <article key={metric.label} className={styles.metricCard}>
                  <p className={styles.metricLabel}>{metric.label}</p>
                  <p className={styles.metricValue}>{metric.value}</p>
                </article>
              ))}
            </div>

            {!isCompact ? (
              <div className={styles.valueBlock}>
                <p className={styles.valueLabel}>{copy.shell.focusAreasLabel}</p>
                <div className={styles.valueList}>
                  {copy.shell.focusAreas.map((value) => (
                    <span key={value} className={styles.valueChip}>
                      {value}
                    </span>
                  ))}
                </div>
              </div>
            ) : null}

            {content.cards.length ? (
              <div className={`${styles.cardGrid} ${isCompact ? styles.cardGridCompact : ''}`}>
                {content.cards.map((card) => (
                  <article key={card.title} className={styles.storyCard}>
                    <p className={styles.cardEyebrow}>{card.eyebrow}</p>
                    <h3 className={styles.cardTitle}>{card.title}</h3>
                    <p className={styles.cardText}>{card.text}</p>
                  </article>
                ))}
              </div>
            ) : null}

            {content.ctas?.length ? (
              <div className={styles.ctaRow}>
                {content.ctas.map((cta) => (
                  <a
                    key={cta.label}
                    className={`${styles.ctaLink} ${cta.tone === 'secondary' ? styles.ctaLinkSecondary : styles.ctaLinkPrimary}`}
                    href={cta.href}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {cta.label}
                  </a>
                ))}
              </div>
            ) : null}

            {content.steps?.length ? (
              <div className={styles.stepSection}>
                <p className={styles.valueLabel}>{copy.shell.expectedFlowLabel}</p>
                <div className={styles.stepList}>
                  {content.steps.map((step, index) => (
                    <article key={step.title} className={styles.stepCard}>
                      <div className={styles.stepNumber}>{index + 1}</div>
                      <div>
                        <h3 className={styles.stepTitle}>{step.title}</h3>
                        <p className={styles.stepText}>{step.text}</p>
                      </div>
                    </article>
                  ))}
                </div>
              </div>
            ) : null}

            <div className={`${styles.storyLinks} ${isCompact ? styles.storyLinksCompact : ''}`}>
              {content.links.map((link) => (
                <a key={link.label} href={link.href} target="_blank" rel="noreferrer">
                  {link.label}
                </a>
              ))}
            </div>
          </div>
        </aside>
      </main>
    </div>
  );
}
